import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  genererCvAnonyme,
  getFormateurDossier,
  getPiecesFormateur,
  urlPieceFormateur,
} from '../services/formateurService';
import PiecesFormateur, {
  AlerteAttestations,
  DepotPieceModal,
  attestationsEchues,
  etatEcheance,
  iconePour,
} from './PiecesFormateur';
import VignetteFichier from '../components/VignetteFichier';
import '../assets/styles/Drive.css';
import { codeFormateur } from './Formateurs';
import { telechargerPdfDocument, urlPdfDocument } from '../services/sessionService';
import ContratFormateurActions from '../components/ContratFormateurActions';
import FormateurFormulaire from './FormateurFormulaire';
import PlanningFormateur from './PlanningFormateur';
import EvaluationsFormateur from './EvaluationsFormateur';
import AccesFormateurModal from './AccesFormateurModal';
import { useAuth } from '../context/AuthContext';
import EmailsEnvoyes from './EmailsEnvoyes';
import { formatDate, formatDateTime, formaterTelephone } from './devisUtils';
import BarreProgression, { useProgression } from '../components/BarreProgression';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Devis.css';
import '../assets/styles/Clients.css';
import '../assets/styles/FormateurFiche.css';
import { IconeTelecharger } from '../components/IconesDocument';
import { toast } from '../components/Toasts';

const TYPES_DOCUMENT = {
  convocation_formateur: 'Convocation formateur',
  feuille_presence: 'Feuille de présence',
  contrat_formateur: 'Contrat formateur',
};

/** Vrai si tous les mots de la recherche figurent dans le texte (sans accents). */
function correspond(recherche, texte) {
  const simple = (t) =>
    String(t || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  return simple(recherche)
    .split(/\s+/)
    .filter(Boolean)
    .every((m) => simple(texte).includes(m));
}

/** 'aaaa-mm-jj' du jour, pour situer une journée sans décalage de fuseau. */
function aujourdhuiIso() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Une journée (ou plus) tombe-t-elle entre deux dates 'aaaa-mm-jj' ? Une borne vide ne limite pas. */
function dansPeriode(jours, du, au) {
  if (!du && !au) return true;
  return (jours || []).some((j) => (!du || j >= du) && (!au || j <= au));
}

/** « 26 nov. 2026 » ou « 12 → 13 oct. 2026 » : les dates d'une session, en bref. */
function periodeCourte(jours) {
  const l = (jours || []).filter(Boolean);
  if (!l.length) return '—';
  if (l.length === 1) return formatDate(l[0]);
  const debut = l[0];
  const fin = l[l.length - 1];
  // Même mois : « 12 → 13 oct. 2026 », pas deux dates entières.
  if (debut.slice(0, 7) === fin.slice(0, 7)) return `${Number(debut.slice(8, 10))} → ${formatDate(fin)}`;
  return `${formatDate(debut)} → ${formatDate(fin)}`;
}

const initialesDe = (f) =>
  [f.prenom, f.nom]
    .filter(Boolean)
    .map((m) => m[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

/** Ce que le formateur a signé, journée par journée. */
function etatJournee(jour, emargements, aujourdhui) {
  const signee = emargements.find((e) => e.jour === jour);
  if (signee) {
    return { libelle: `signée le ${formatDateTime(signee.signeLe)}`, classe: ' devis-pill-valide' };
  }
  if (jour <= aujourdhui) return { libelle: 'à signer', classe: ' devis-pill-attente' };
  return { libelle: 'à venir', classe: '' };
}

function champsFiche(f, espaceFormateur = false) {
  const champs = [
    ['Nom', [f.civilite, f.prenom, f.nom].filter(Boolean).join(' ')],
    ['Email', f.email],
    ['Téléphone', formaterTelephone(f.telephone)],
    ['Fonction', f.fonction],
    ['Spécialités', f.specialites],
    ['Adresse', f.adresse],
  ];
  // Les notes et l'état de la fiche sont ceux de l'équipe.
  if (!espaceFormateur) {
    champs.push(
      [
        'Rémunération par journée',
        f.tarifJournalierHt ? `${Number(f.tarifJournalierHt).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € HT` : '',
      ],
      ['Notes internes', f.notes],
      ['État', f.actif ? 'Actif — proposé à l’assignation' : 'Retiré']
    );
  }
  return champs;
}

/** Les pièces que la fiche résume, dans l'ordre où on les demande. */
const PIECES_RESUMEES = [
  ['cv', 'CV'],
  ['cv_anonyme', 'CV anonyme'],
  ['urssaf', 'Attestation URSSAF'],
  ['rc_pro', 'Assurance RC Pro'],
  ['diplome', 'Diplômes'],
  ['permis', 'Permis de conduire'],
  ['carte_grise', 'Carte grise'],
];

/** Les fichiers d'une nature, chacun ouvrable, avec l'état de son échéance. */
function LiensPieces({ pieces }) {
  if (!pieces.length) return '—';
  return pieces.map((p) => {
    const etat = etatEcheance(p.dateEcheance);
    return (
      <div key={p.id} className="formateur-fiche-piece">
        <a
          href={urlPieceFormateur(p.id, { apercu: true })}
          target="_blank"
          rel="noopener noreferrer"
          title={p.nomFichier}
        >
          {p.libelle && p.libelle !== p.typeLibelle ? p.libelle : p.nomFichier}
        </a>
        {etat && <span className={`devis-status-pill${etat.classe}`}>{etat.libelle}</span>}
      </div>
    );
  });
}

/**
 * Fiche d'un formateur : ses informations, les journées qu'il anime avec
 * l'état de son émargement, et les documents produits pour lui — sa
 * convocation et les feuilles de présence. Même gabarit que le dossier
 * client, la fiche elle-même se lisant de haut en bas : un champ par ligne,
 * puis ses pièces.
 */
function FormateurFiche({ formateurId = null, espaceFormateur = false }) {
  const params = useParams();
  // Dans l'espace formateur, la fiche est celle du compte, pas de l'adresse.
  const id = formateurId ?? params.id;
  const navigate = useNavigate();
  const { utilisateur } = useAuth();
  const [accesOuvert, setAccesOuvert] = useState(false);
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onglet, setOnglet] = useState('fiche');
  const [edition, setEdition] = useState(false);
  const [message, setMessage] = useState(null);
  const [travail, setTravail] = useState(null);
  // Pièces administratives (migration 080) : CV, URSSAF, RC Pro, diplôme…
  const [pieces, setPieces] = useState([]);
  // Le formulaire de dépôt d'une pièce : fermé (null) ou ouvert sur une nature.
  const [depotPiece, setDepotPiece] = useState(null);
  // Onglet Formations : recherche, tri par date, période, et lignes déroulées.
  const [rechercheFormations, setRechercheFormations] = useState('');
  const [triFormations, setTriFormations] = useState('asc');
  const [formationsDu, setFormationsDu] = useState('');
  const [formationsAu, setFormationsAu] = useState('');
  const [ouvertes, setOuvertes] = useState(() => new Set());
  // Onglet Documents : recherche, nature, client et période.
  const [rechercheDocs, setRechercheDocs] = useState('');
  const [filtreTypeDoc, setFiltreTypeDoc] = useState('');
  const [filtreClientDoc, setFiltreClientDoc] = useState('');
  const [documentsDu, setDocumentsDu] = useState('');
  const [documentsAu, setDocumentsAu] = useState('');
  // Onglet Contrats : recherche, et état de la journée (à venir, passée, sans contrat).
  const [rechercheContrats, setRechercheContrats] = useState('');
  const [filtreContrats, setFiltreContrats] = useState('');

  // Régénération du CV anonyme, depuis l'onglet Fiche comme depuis les pièces.
  const [generationCv, setGenerationCv] = useState(false);
  const progressionCv = useProgression(generationCv, { estimeeMs: 25000 });

  const aUnCv = pieces.some((piece) => piece.type === 'cv');
  const aUnCvAnonyme = pieces.some((piece) => piece.type === 'cv_anonyme');

  const chargerPieces = useCallback(
    () =>
      getPiecesFormateur(id)
        .then((res) => setPieces(res.data || []))
        .catch(() => setPieces([])),
    [id]
  );

  const charger = useCallback(() => {
    setLoading(true);
    setError(null);
    chargerPieces();
    return getFormateurDossier(id)
      .then((res) => setDossier(res.data))
      .catch((err) => setError(err.message || 'Formateur introuvable.'))
      .finally(() => setLoading(false));
  }, [id, chargerPieces]);

  useEffect(() => {
    charger();
  }, [charger]);

  async function telecharger(doc) {
    setTravail(doc.id);
    setMessage(null);
    try {
      await telechargerPdfDocument(doc.id);
    } catch (err) {
      setMessage(err.message || 'Téléchargement impossible.');
    } finally {
      setTravail(null);
    }
  }

  /** Refait le CV anonyme à partir du dernier CV déposé (lecture par l'IA). */
  async function regenererCv() {
    setGenerationCv(true);
    setMessage(null);
    try {
      await genererCvAnonyme(id);
      await chargerPieces();
    } catch (err) {
      setMessage(err.message || 'Génération du CV anonyme impossible.');
    } finally {
      setGenerationCv(false);
    }
  }

  const aujourdhui = aujourdhuiIso();
  const sessions = dossier ? dossier.sessions : [];
  const documents = sessions.flatMap((s) => s.documents.map((d) => ({ ...d, session: s })));
  // Les formations, de la plus proche à la plus lointaine (ou l'inverse), entre deux dates si on le demande.
  const premierJour = (s) => (s.jours && s.jours[0]) || '';
  const sessionsAffichees = sessions
    .filter(
      (s) =>
        dansPeriode(s.jours, formationsDu, formationsAu) &&
        correspond(
          rechercheFormations,
          [s.intitule, s.groupeLibelle, s.societeNom, s.contratNumero, s.lieu, s.modalite].join(' ')
        )
    )
    .sort((a, b) =>
      triFormations === 'asc'
        ? premierJour(a).localeCompare(premierJour(b))
        : premierJour(b).localeCompare(premierJour(a))
    );
  const clientsDocuments = [...new Set(documents.map((d) => d.session.societeNom).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, 'fr')
  );
  const documentsAffiches = documents.filter(
    (d) =>
      (!filtreTypeDoc || d.type === filtreTypeDoc) &&
      (!filtreClientDoc || d.session.societeNom === filtreClientDoc) &&
      dansPeriode(d.session.jours, documentsDu, documentsAu) &&
      correspond(
        rechercheDocs,
        [
          TYPES_DOCUMENT[d.type] || d.type,
          d.session.intitule,
          d.session.societeNom,
          d.session.contratNumero,
          (d.session.jours || []).join(' '),
        ].join(' ')
      )
  );
  // Ses contrats : une ligne par journée où il intervient, avec le contrat produit pour elle.
  const contrats = sessions.map((s) => ({
    session: s,
    document: s.documents.find((d) => d.type === 'contrat_formateur') || null,
    // Une journée est « à venir » tant que sa dernière date n'est pas passée.
    passee: (s.jours || []).length > 0 && s.jours[s.jours.length - 1] < aujourdhui,
  }));
  const contratsAffiches = contrats.filter(
    (c) =>
      (filtreContrats === '' ||
        (filtreContrats === 'a_venir' && !c.passee) ||
        (filtreContrats === 'passees' && c.passee) ||
        (filtreContrats === 'sans_contrat' && !c.document)) &&
      correspond(
        rechercheContrats,
        [
          c.session.intitule,
          c.session.groupeLibelle,
          c.session.societeNom,
          c.session.contratNumero,
          c.session.lieu,
          (c.session.jours || []).map(formatDate).join(' '),
        ].join(' ')
      )
  );
  const nomComplet = dossier ? [dossier.prenom, dossier.nom].filter(Boolean).join(' ') : '';

  const ONGLETS = dossier
    ? [
        { cle: 'fiche', label: 'Fiche' },
        { cle: 'pieces', label: 'Pièces administratives', compte: pieces.length },
        // Le calendrier des formations à tenir, juste avant leur liste.
        { cle: 'planning', label: 'Planning' },
        { cle: 'formations', label: 'Formations', compte: sessions.length },
        { cle: 'documents', label: 'Documents', compte: documents.length },
        // Les contrats de prestation, journée par journée (migration 107).
        { cle: 'contrats', label: 'Contrats', compte: contrats.filter((c) => c.document).length },
        // La note globale de ses stagiaires, formation par formation.
        { cle: 'evaluations', label: 'Évaluations' },
        { cle: 'emails', label: 'Emails' },
      ]
    : [];

  return (
    <div className="devis-page devis-page-scroll">
      <div className="ff-entete">
        {!espaceFormateur && (
          <button type="button" className="clients-retour ff-retour" onClick={() => navigate('/formateurs')}>
            ← Formateurs
          </button>
        )}
        <div className="ff-entete-corps">
          <div className="ff-avatar" aria-hidden="true">
            {dossier ? initialesDe(dossier) : '…'}
          </div>
          <div className="ff-identite">
            <h1 className="ff-nom">
              {dossier && (
                <span
                  className="formateur-code formateur-code-grand"
                  title="Code du formateur : initiales + numéro de fiche. C'est lui qui identifie son CV anonyme."
                >
                  {codeFormateur(dossier)}
                </span>
              )}
              {nomComplet || 'Formateur'}
            </h1>
            {dossier && (
              <div className="ff-puces">
                <span className={`devis-status-pill ${dossier.actif ? 'devis-pill-valide' : ''}`}>
                  {dossier.actif ? 'Actif' : 'Retiré'}
                </span>
                {dossier.fonction && <span className="ff-puce">{dossier.fonction}</span>}
                {dossier.email && (
                  <a className="ff-puce ff-puce-lien" href={`mailto:${dossier.email}`}>
                    {dossier.email}
                  </a>
                )}
                {dossier.telephone && <span className="ff-puce">{formaterTelephone(dossier.telephone)}</span>}
              </div>
            )}
          </div>
          {dossier && (
            <div className="ff-entete-actions">
              <button type="button" className="devis-btn" onClick={() => setEdition(true)}>
                Modifier la fiche
              </button>
              {/* Identifiant et mot de passe de connexion : à la main du
                  formateur seulement, depuis son espace. */}
              {espaceFormateur && (
                <button
                  type="button"
                  className="devis-btn"
                  onClick={() => setAccesOuvert(true)}
                  title="Modifier mon identifiant ou mon mot de passe"
                >
                  ⚙ Paramètres d’accès
                </button>
              )}
            </div>
          )}
        </div>
        {dossier && (
          <div className="ff-chiffres">
            <div className="ff-chiffre">
              <span className="ff-chiffre-valeur">{sessions.length}</span>
              <span className="ff-chiffre-libelle">formation{sessions.length > 1 ? 's' : ''}</span>
            </div>
            <div className="ff-chiffre">
              <span className="ff-chiffre-valeur">{sessions.filter((s) => (s.jours || []).slice(-1)[0] >= aujourdhui).length}</span>
              <span className="ff-chiffre-libelle">à venir</span>
            </div>
            <div className="ff-chiffre">
              <span className="ff-chiffre-valeur">{contrats.filter((c) => c.document).length}</span>
              <span className="ff-chiffre-libelle">contrat{contrats.filter((c) => c.document).length > 1 ? 's' : ''}</span>
            </div>
            <div className="ff-chiffre">
              <span className="ff-chiffre-valeur">{pieces.filter((p) => p.type !== 'cv_anonyme').length}</span>
              <span className="ff-chiffre-libelle">pièce{pieces.length > 1 ? 's' : ''}</span>
            </div>
          </div>
        )}
      </div>

      {loading && <div className="devis-state">Chargement de la fiche...</div>}
      {!loading && error && <div className="devis-state devis-state-error">{error}</div>}
      {message && <div className="devis-state devis-state-error">{message}</div>}

      {!loading && dossier && (
        <>
          {espaceFormateur && utilisateur?.mot_de_passe_par_defaut && (
            <div className="espace-formateur-alerte">
              <span>
                Vous utilisez encore le mot de passe par défaut : pensez à le remplacer par le vôtre.
              </span>
              <button type="button" className="devis-btn devis-btn-sm" onClick={() => setAccesOuvert(true)}>
                Changer mon mot de passe
              </button>
            </div>
          )}

          {dossier.actif && <AlerteAttestations echues={attestationsEchues(pieces)} />}

          <nav className="clients-onglets">
            {ONGLETS.map((o) => (
              <button
                key={o.cle}
                type="button"
                className={'clients-onglet' + (onglet === o.cle ? ' clients-onglet-actif' : '')}
                onClick={() => setOnglet(o.cle)}
              >
                {o.label}
                {o.compte !== undefined && <span className="clients-onglet-compte">{o.compte}</span>}
              </button>
            ))}
          </nav>

          {/* ---------------- Fiche ---------------- */}
          {onglet === 'fiche' && (
            <div className="ff-grille">
              <section className="devis-card ff-carte">
                <div className="ff-carte-titre">Informations</div>
                <div className="ff-champs">
                  {champsFiche(dossier, espaceFormateur).map(([libelle, valeur]) => (
                    <div className="ff-champ" key={libelle}>
                      <div className="ff-champ-libelle">{libelle}</div>
                      <div className="ff-champ-valeur">{valeur || '—'}</div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="devis-card ff-carte">
                <div className="ff-carte-titre">Pièces</div>
                <div className="ff-pieces">
                  {PIECES_RESUMEES.map(([type, libelle]) => {
                    const deposees = pieces.filter((p) => p.type === type);
                    const manque = deposees.length === 0 && type !== 'cv_anonyme';
                    return (
                      <div className={`ff-piece${manque ? ' ff-piece-manque' : ''}`} key={type}>
                        <div className="ff-piece-tete">
                          <span className="ff-piece-nom">{libelle}</span>
                          {/* Une ligne sans fichier propose de le déposer, sur la bonne nature. */}
                          {manque && (
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-btn-primary"
                              onClick={() => setDepotPiece({ type })}
                            >
                              Déposer
                            </button>
                          )}
                        </div>
                        <div className="ff-piece-valeur">
                          {manque ? (
                            <span className="ff-piece-absent">Fichier manquant</span>
                          ) : (
                            <LiensPieces pieces={deposees} />
                          )}
                          {/* Le CV anonyme se refait d'ici : après correction de
                              la fiche ou dépôt d'un nouveau CV. */}
                          {type === 'cv_anonyme' && aUnCv && (
                            <div className="formateur-fiche-action">
                              <button
                                type="button"
                                className="devis-btn devis-btn-sm"
                                onClick={regenererCv}
                                disabled={generationCv}
                                title="Relit le dernier CV déposé et refait le CV anonyme (IA)"
                              >
                                {generationCv
                                  ? 'Génération…'
                                  : aUnCvAnonyme
                                    ? 'Régénérer le CV anonyme'
                                    : 'Générer le CV anonyme'}
                              </button>
                            </div>
                          )}
                          {type === 'cv_anonyme' && !aUnCv && (
                            <div className="clients-ligne-detail">
                              Déposez d’abord un CV : le CV anonyme en est tiré.
                            </div>
                          )}
                          {type === 'cv_anonyme' && (
                            <BarreProgression
                              valeur={progressionCv}
                              libelle="Génération du CV anonyme par l’IA"
                              estimation
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>
          )}

          {/* ---------------- Planning ---------------- */}
          {onglet === 'planning' && <PlanningFormateur sessions={sessions} />}

          {/* ---------------- Formations ----------------
              Une ligne par formation, dont le détail se déroule : triées par
              date, avec une période « du … au … » et une recherche. */}
          {onglet === 'formations' && sessions.length === 0 && (
            <div className="devis-card clients-vide">
              Aucune journée assignée (assignation dans la planification d’un contrat).
            </div>
          )}
          {onglet === 'formations' && sessions.length > 0 && (
            <div className="ff-filtres">
              <input
                type="search"
                className="devis-search"
                placeholder="Rechercher une formation (client, contrat, lieu...)"
                value={rechercheFormations}
                onChange={(e) => setRechercheFormations(e.target.value)}
              />
              <label className="ff-filtre-date">
                <span>Du</span>
                <input type="date" value={formationsDu} max={formationsAu || undefined} onChange={(e) => setFormationsDu(e.target.value)} />
              </label>
              <label className="ff-filtre-date">
                <span>Au</span>
                <input type="date" value={formationsAu} min={formationsDu || undefined} onChange={(e) => setFormationsAu(e.target.value)} />
              </label>
              <button
                type="button"
                className="devis-btn devis-btn-sm"
                onClick={() => setTriFormations((t) => (t === 'asc' ? 'desc' : 'asc'))}
                title="Inverser l’ordre des dates"
              >
                Date {triFormations === 'asc' ? '↑ croissante' : '↓ décroissante'}
              </button>
              {(formationsDu || formationsAu || rechercheFormations) && (
                <button
                  type="button"
                  className="devis-btn devis-btn-sm"
                  onClick={() => {
                    setFormationsDu('');
                    setFormationsAu('');
                    setRechercheFormations('');
                  }}
                >
                  Réinitialiser
                </button>
              )}
              <span className="ff-compte">
                {sessionsAffichees.length} sur {sessions.length}
              </span>
            </div>
          )}
          {onglet === 'formations' && sessions.length > 0 && sessionsAffichees.length === 0 && (
            <div className="devis-card clients-vide">Aucune formation ne correspond.</div>
          )}
          {onglet === 'formations' && sessionsAffichees.length > 0 && (
            <div className="devis-card ff-formations">
              <div className="fg-liste">
              <div className="fg-ligne fg-ligne-formation fg-entete" aria-hidden="true">
                <div>CONTRAT · FORMATION</div>
                <div>CLIENT</div>
                <div>DATES</div>
                <div>LIEU</div>
                <div>ÉTAT</div>
                <div />
              </div>
              {sessionsAffichees.map((s) => {
                const ouverte = ouvertes.has(s.id);
                const passee = (s.jours || []).length > 0 && s.jours[s.jours.length - 1] < aujourdhui;
                return (
                  <div className={`ff-formation${ouverte ? ' ff-formation-ouverte' : ''}`} key={s.id}>
                    {/* La même ligne que l'onglet Contrats : une colonne par information,
                        chacune nommée en petit sur mobile ; « Détails » déroule le reste. */}
                    <div className="fg-ligne fg-ligne-formation">
                      <div className="fg-cellule" data-label="Contrat et formation">
                        <span className="fg-contrat" title={`Contrat ${s.contratNumero}`}>
                          {s.contratNumero}
                        </span>
                        <span className="fg-texte fg-formation" title={s.intitule || ''}>
                          {s.intitule || '—'}
                        </span>
                        {s.groupeLibelle && <span className="fg-sous">{s.groupeLibelle}</span>}
                      </div>
                      <div className="fg-cellule" data-label="Client">
                        <span className="fg-texte" title={s.societeNom || ''}>
                          {s.societeNom || '—'}
                        </span>
                      </div>
                      <div className="fg-cellule" data-label="Dates">
                        <span className="fg-texte ff-dates">{periodeCourte(s.jours)}</span>
                        <span className="fg-sous">
                          {(s.jours || []).length} journée{(s.jours || []).length > 1 ? 's' : ''}
                          {s.heureDebut && s.heureFin ? ` · ${s.heureDebut}–${s.heureFin}` : ''}
                        </span>
                      </div>
                      <div className="fg-cellule" data-label="Lieu">
                        <span className="fg-texte" title={s.lieu || ''}>
                          {s.modalite === 'distanciel' ? 'À distance' : s.lieu || '—'}
                        </span>
                      </div>
                      <div className="fg-cellule" data-label="État">
                        <span className={`devis-status-pill ff-etat${passee ? '' : ' devis-pill-attente'}`}>
                          {passee ? 'passée' : 'à venir'}
                        </span>
                      </div>
                      <div className="fg-cellule fg-actions">
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm"
                          aria-expanded={ouverte}
                          onClick={() =>
                            setOuvertes((prec) => {
                              const suite = new Set(prec);
                              if (suite.has(s.id)) suite.delete(s.id);
                              else suite.add(s.id);
                              return suite;
                            })
                          }
                        >
                          {ouverte ? 'Masquer ▴' : 'Détails ▾'}
                        </button>
                      </div>
                    </div>

                    {ouverte && (
                      <div className="ff-formation-detail">
                        <div className="clients-demande-sous">
                          {[s.societeNom, `contrat ${s.contratNumero}`, s.lieu || s.modalite].filter(Boolean).join(' · ')}
                          {s.heureDebut && s.heureFin ? ` · ${s.heureDebut}–${s.heureFin}` : ''}
                        </div>

                        <div className="clients-section">
                          <div className="clients-section-titre">Journées et émargement</div>
                          {s.jours.map((jour) => {
                            const etat = etatJournee(jour, s.emargements, aujourdhui);
                            return (
                              <div className="clients-ligne" key={jour}>
                                <div className="clients-ligne-texte">
                                  <div>{formatDate(jour)}</div>
                                </div>
                                <span className={`devis-status-pill${etat.classe}`}>{etat.libelle}</span>
                              </div>
                            );
                          })}
                        </div>

                        {s.documents.length > 0 && (
                          <div className="clients-section">
                            <div className="clients-section-titre">Documents</div>
                            {s.documents.map((d) => (
                              <div className="clients-ligne" key={d.id}>
                                <div className="clients-ligne-texte">
                                  <div>{TYPES_DOCUMENT[d.type] || d.type}</div>
                                  <div className="clients-ligne-detail">
                                    généré le {formatDateTime(d.genereLe)}
                                    {d.type === 'convocation_formateur'
                                      ? d.dateEnvoi
                                        ? ` · envoyée le ${formatDateTime(d.dateEnvoi)}`
                                        : ' · pas encore envoyée'
                                      : ''}
                                  </div>
                                </div>
                                <div className="clients-ligne-actions">
                                  <button
                                    type="button"
                                    className="devis-btn devis-btn-sm"
                                    onClick={() => telecharger(d)}
                                    disabled={travail === d.id}
                                  >
                                    {travail === d.id ? 'En cours' : (<><IconeTelecharger />PDF</>)}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            </div>
          )}

          {/* ---------------- Pièces administratives ---------------- */}
          {onglet === 'pieces' && (
            <PiecesFormateur
              formateurId={Number(id)}
              pieces={pieces}
              onChange={chargerPieces}
              onDeposer={(type) => setDepotPiece({ type })}
            />
          )}

          {/* ---------------- Documents ---------------- */}
          {onglet === 'documents' && documents.length === 0 && (
            <div className="devis-card clients-vide">
              Aucun document pour l’instant (générés depuis la planification).
            </div>
          )}
          {onglet === 'documents' && documents.length > 0 && (
            <div className="devis-card clients-demande">
              <div className="clients-section">
                <div className="ff-filtres ff-filtres-carte">
                  <input
                    type="search"
                    className="devis-search"
                    placeholder="Rechercher un document (client, contrat, dates...)"
                    value={rechercheDocs}
                    onChange={(e) => setRechercheDocs(e.target.value)}
                  />
                  <select
                    className="drive-filtre"
                    value={filtreTypeDoc}
                    onChange={(e) => setFiltreTypeDoc(e.target.value)}
                    aria-label="Filtrer par nature"
                  >
                    <option value="">Tous les documents</option>
                    <option value="convocation_formateur">Convocation formateur</option>
                    <option value="feuille_presence">Feuille de présence</option>
                    <option value="contrat_formateur">Contrat formateur</option>
                  </select>
                  <select
                    className="drive-filtre"
                    value={filtreClientDoc}
                    onChange={(e) => setFiltreClientDoc(e.target.value)}
                    aria-label="Filtrer par client"
                  >
                    <option value="">Tous les clients</option>
                    {clientsDocuments.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <label className="ff-filtre-date">
                    <span>Du</span>
                    <input type="date" value={documentsDu} max={documentsAu || undefined} onChange={(e) => setDocumentsDu(e.target.value)} />
                  </label>
                  <label className="ff-filtre-date">
                    <span>Au</span>
                    <input type="date" value={documentsAu} min={documentsDu || undefined} onChange={(e) => setDocumentsAu(e.target.value)} />
                  </label>
                  {(rechercheDocs || filtreTypeDoc || filtreClientDoc || documentsDu || documentsAu) && (
                    <button
                      type="button"
                      className="devis-btn devis-btn-sm"
                      onClick={() => {
                        setRechercheDocs('');
                        setFiltreTypeDoc('');
                        setFiltreClientDoc('');
                        setDocumentsDu('');
                        setDocumentsAu('');
                      }}
                    >
                      Réinitialiser
                    </button>
                  )}
                  <span className="ff-compte">
                    {documentsAffiches.length} sur {documents.length}
                  </span>
                </div>
                {documentsAffiches.length === 0 && (
                  <div className="clients-ligne-detail">Aucun document ne correspond.</div>
                )}
                {/* Le même dessin que les documents des clients : l'aperçu du
                    PDF lui-même, ouvrable d'un clic. */}
                <div className="drive-grille">
                  {documentsAffiches.map((d) => (
                    <article className="drive-tuile" key={d.id}>
                      <div
                        className="drive-tuile-visuel drive-tuile-visuel-apercu"
                        role="button"
                        tabIndex={0}
                        title="Ouvrir"
                        onClick={() => window.open(urlPdfDocument(d.id, { apercu: true }), '_blank')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            window.open(urlPdfDocument(d.id, { apercu: true }), '_blank');
                          }
                        }}
                      >
                        <VignetteFichier
                          url={urlPdfDocument(d.id, { apercu: true })}
                          mime="application/pdf"
                          alt={`Aperçu de ${TYPES_DOCUMENT[d.type] || d.type}`}
                          secours={iconePour('application/pdf')}
                          largeur={232}
                        />
                      </div>
                      <div className="drive-tuile-corps">
                        <div className="drive-tuile-nom" title={TYPES_DOCUMENT[d.type] || d.type}>
                          {TYPES_DOCUMENT[d.type] || d.type}
                        </div>
                        <div className="drive-tuile-meta">
                          {[d.session.societeNom, d.session.intitule].filter(Boolean).join(' · ')}
                        </div>
                        <div className="drive-tuile-meta">
                          {d.session.jours.map(formatDate).join(', ')}
                        </div>
                        <div className="drive-tuile-meta">
                          généré le {formatDateTime(d.genereLe)}
                          {d.type === 'convocation_formateur'
                            ? d.dateEnvoi
                              ? ` · envoyée le ${formatDateTime(d.dateEnvoi)}`
                              : ' · pas encore envoyée'
                            : ''}
                        </div>
                      </div>
                      <div className="drive-tuile-actions">
                        <button
                          type="button"
                          className="drive-action"
                          onClick={() =>
                            window.open(urlPdfDocument(d.id, { apercu: true }), '_blank')
                          }
                          title="Ouvrir"
                        >
                          Ouvrir
                        </button>
                        <button
                          type="button"
                          className="drive-action"
                          onClick={() => telecharger(d)}
                          disabled={travail === d.id}
                          title="Télécharger"
                        >
                          {travail === d.id ? 'En cours' : 'Télécharger'}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ---------------- Contrats ----------------
              Le contrat de prestation de chaque journée où il intervient :
              recherche, filtre par état, voir et télécharger. */}
          {onglet === 'contrats' && contrats.length === 0 && (
            <div className="devis-card clients-vide">
              Aucun contrat pour l’instant : il est produit avec la planification, dès que ce formateur est
              assigné à une journée.
            </div>
          )}
          {onglet === 'contrats' && contrats.length > 0 && (
            <div className="devis-card clients-demande">
              <div className="clients-section">
                <div className="devis-toolbar" style={{ marginTop: 0, marginBottom: 10 }}>
                  <input
                    type="search"
                    className="devis-search"
                    placeholder="Rechercher un contrat (client, formation, dates...)"
                    value={rechercheContrats}
                    onChange={(e) => setRechercheContrats(e.target.value)}
                  />
                  <select
                    className="drive-filtre"
                    value={filtreContrats}
                    onChange={(e) => setFiltreContrats(e.target.value)}
                    aria-label="Filtrer les contrats"
                  >
                    <option value="">Toutes les journées</option>
                    <option value="a_venir">À venir</option>
                    <option value="passees">Passées</option>
                    <option value="sans_contrat">Contrat à générer</option>
                  </select>
                </div>
                {contratsAffiches.length === 0 && (
                  <div className="clients-ligne-detail">Aucun contrat ne correspond.</div>
                )}
                {contratsAffiches.length > 0 && (
                  <div className="fg-liste">
                    <div className="fg-ligne fg-ligne-contrat fg-entete" aria-hidden="true">
                      <div>CONTRAT · FORMATION</div>
                      <div>CLIENT</div>
                      <div>DATES</div>
                      <div>LIEU</div>
                      <div>CONTRAT DU FORMATEUR</div>
                      <div />
                    </div>
                    {contratsAffiches.map(({ session: s, document, passee }) => (
                      <div className="fg-ligne fg-ligne-contrat" key={s.id}>
                        <div className="fg-cellule" data-label="Contrat et formation">
                          <span className="fg-contrat" title={`Contrat ${s.contratNumero}`}>
                            {s.contratNumero}
                          </span>
                          <span className="fg-texte fg-formation" title={s.intitule || ''}>
                            {s.intitule || '—'}
                          </span>
                          {s.groupeLibelle && <span className="fg-sous">{s.groupeLibelle}</span>}
                        </div>
                        <div className="fg-cellule" data-label="Client">
                          <span className="fg-texte" title={s.societeNom || ''}>
                            {s.societeNom || '—'}
                          </span>
                        </div>
                        <div className="fg-cellule" data-label="Dates">
                          <span className="fg-texte">{(s.jours || []).map(formatDate).join(', ') || '—'}</span>
                          <span className="fg-sous">{passee ? 'passée' : 'à venir'}</span>
                        </div>
                        <div className="fg-cellule" data-label="Lieu">
                          <span className="fg-texte" title={s.lieu || ''}>
                            {s.modalite === 'distanciel' ? 'À distance' : s.lieu || '—'}
                          </span>
                        </div>
                        <div className="fg-cellule" data-label="Contrat du formateur">
                          <ContratFormateurActions
                            sessionId={s.id}
                            document={document ? { id: document.id, le: document.genereLe } : null}
                            // Le formateur lit son contrat ; l'équipe le produit.
                            peutGenerer={!espaceFormateur}
                            titre={`Contrat formateur — ${nomComplet}`}
                            sousTitre={[s.contratNumero, s.societeNom, s.intitule].filter(Boolean).join(' · ')}
                            onChange={charger}
                          />
                        </div>
                        <div className="fg-cellule fg-actions" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ---------------- Évaluations ----------------
              Les résultats des QCM de fin de formation de ses stagiaires. */}
          {onglet === 'evaluations' && <EvaluationsFormateur formateurId={Number(id)} />}

          {/* ---------------- Emails ----------------
              Ce qui lui a été envoyé par email (convocations, liens
              d'émargement…), tel que reçu. */}
          {onglet === 'emails' && (
            <div className="devis-card clients-demande">
              <EmailsEnvoyes filtres={{ formateurId: id }} />
            </div>
          )}
        </>
      )}

      {edition && dossier && (
        <FormateurFormulaire
          formateur={dossier}
          espaceFormateur={espaceFormateur}
          onClose={() => setEdition(false)}
          onEnregistre={() => {
            setEdition(false);
            charger();
          }}
        />
      )}

      {accesOuvert && <AccesFormateurModal onClose={() => setAccesOuvert(false)} />}

      {depotPiece && (
        <DepotPieceModal
          formateurId={Number(id)}
          typeInitial={depotPiece.type}
          onClose={() => setDepotPiece(null)}
          onDepose={({ avertissement }) => {
            setDepotPiece(null);
            if (avertissement) toast.erreur(avertissement);
            chargerPieces();
          }}
        />
      )}
    </div>
  );
}

export default FormateurFiche;
